export { Toaster } from './Toast.tsx'
export type { ToasterProps } from './Toast.tsx'

import { toast as sonnerToast, type ExternalToast } from 'sonner'

/**
 * Injects `--toast-duration` CSS variable when a per-toast `duration` is set,
 * so the progress bar animation stays in sync with the actual dismiss time.
 */
function withDuration(opts?: ExternalToast): ExternalToast | undefined {
  if (!opts?.duration) return opts
  // Finite duration → sync progress bar animation; Infinity → 0ms hides bar instantly
  const cssValue = Number.isFinite(opts.duration) ? `${opts.duration}ms` : '0ms'
  return {
    ...opts,
    style: { '--toast-duration': cssValue, ...opts.style },
  } as ExternalToast
}

type MessageFn = (message: Parameters<typeof sonnerToast>[0], data?: ExternalToast) => string | number

function wrap(fn: MessageFn): MessageFn {
  return (message, data) => fn(message, withDuration(data))
}

export const toast = Object.assign(
  wrap(sonnerToast),
  {
    success: wrap(sonnerToast.success),
    error: wrap(sonnerToast.error),
    warning: wrap(sonnerToast.warning),
    info: wrap(sonnerToast.info),
    loading: wrap(sonnerToast.loading),
    message: wrap(sonnerToast.message),
    custom: ((jsx: Parameters<typeof sonnerToast.custom>[0], data?: ExternalToast) =>
      sonnerToast.custom(jsx, withDuration(data))) as typeof sonnerToast.custom,
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  },
) as typeof sonnerToast
